/**
 * Venue-locked altitude math.
 *
 * An event stores `pin_floor_alt_m`: the phone GPS altitude (WGS84, the same
 * frame the phone reports) of the venue floor at the pin. The overlay then
 * sits at a fixed world height for every viewer instead of following the
 * viewer's first GPS reading. The viewer's current altitude is the pose
 * filter's one-dimensional Kalman estimate (see pose_filter.js).
 *
 * That filter is deliberately coarse: it initialises with an 8 m sigma floor
 * and gates innovations at 3 sigma, because vertical GPS is multipath-prone.
 * Reading it as an exact eye height put the QA HUD at -3.0 m and +4.3 m in the
 * field (docs/reviews/20261021_qa_corpus_analysis.md §3), so the derived height
 * is clamped to a plausible eye band and an untrusted estimate falls back to
 * `EYE_ASSUMED_M`. Clamping the reader is not a filter retune: the constraint
 * in docs/constraints/20261005_kalman_isolation.md still holds.
 */

export const EYE_ASSUMED_M = 1.5
/** A person holding a phone is somewhere in this band above the floor. */
export const EYE_MIN_M = 1.0
export const EYE_MAX_M = 2.0
/** Above this filter sigma the estimate is not an eye height, it is a guess. */
export const EYE_TRUST_SIGMA_M = 8

export function floorAltM(pin) {
  const raw = pin && pin.pin_floor_alt_m
  if (raw == null) return null
  const value = Number(raw)
  return Number.isFinite(value) ? value : null
}

export function clampEyeHeight(value) {
  if (!Number.isFinite(value)) return EYE_ASSUMED_M
  return Math.min(EYE_MAX_M, Math.max(EYE_MIN_M, value))
}

export function heightAboveFloorM({floorAltM, altM, sigmaM, fallbackM} = {}) {
  if (floorAltM == null) return fallbackM
  if (!Number.isFinite(altM)) return EYE_ASSUMED_M
  // An untrusted vertical estimate is worse than the assumption: a 3 m error
  // at 14 m is 11 degrees of aim on a 15 m pin.
  if (Number.isFinite(sigmaM) && sigmaM > EYE_TRUST_SIGMA_M) return EYE_ASSUMED_M
  return clampEyeHeight(altM - floorAltM)
}
